import React, { useState, useEffect } from 'react';
import { Salad, Flame, UtensilsCrossed, Beef, BookOpen, Sandwich, Wine, Cake, ChevronDown, ChevronUp, GlassWater, Coffee, Beer, Grape, Download } from 'lucide-react';
import { menuCategories, images } from '../data/mock';
import { useLanguage } from '../context/LanguageContext';

const iconMap = {
  Salad,
  Flame,
  UtensilsCrossed,
  Beef,
  BookOpen,
  Sandwich,
  Wine,
  Cake,
  GlassWater,
  Coffee,
  Beer,
  Grape,
};

const MenuPage = () => {
  const [activeCategory, setActiveCategory] = useState(menuCategories[0].id);
  const [expandedMobile, setExpandedMobile] = useState(null);
  const { t, lang } = useLanguage();
  const L = (obj, f) => (lang === 'en' && obj[`${f}_en`]) || obj[f];

  useEffect(() => {
    document.title = 'Menu - Ya Habibi! Restaurant Libanais Genève | Lebanese Menu Geneva';
  }, []);

  const scrollToCategory = (id) => {
    setActiveCategory(id);
    const element = document.getElementById(id);
    if (element) {
      const offset = 150;
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({ top: elementPosition - offset, behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen pt-24 bg-[#f8fafc]">
      {/* Hero */}
      <section className="bg-[#F5E6D3] py-16">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h1 className="font-serif text-4xl md:text-5xl font-bold text-[#0f172a] mb-4">
            {t('menu', 'title')}
          </h1>
          <p className="text-lg text-[#0f172a]/80 max-w-2xl mx-auto mb-8">
            {t('menu', 'subtitle')}
          </p>
          <a
            href="/carte_yahabibi.pdf"
            download="Carte_Ya_Habibi.pdf"
            className="inline-flex items-center gap-2 bg-[#0f172a] text-white px-6 py-3 rounded-md font-semibold hover:bg-[#1e293b] transition-all duration-200 hover:-translate-y-1 shadow-lg"
            data-testid="download-menu-pdf"
          >
            <Download size={20} />
            {t('menu', 'downloadPdf')}
          </a>
        </div>
      </section>

      {/* Category Navigation */}
      <nav className="sticky top-20 z-40 bg-white shadow-md">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex overflow-x-auto scrollbar-hide py-4 gap-2">
            {menuCategories.map((category) => {
              const Icon = iconMap[category.icon];
              return (
                <button
                  key={category.id}
                  onClick={() => scrollToCategory(category.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full whitespace-nowrap transition-all duration-200 ${
                    activeCategory === category.id
                      ? 'bg-[#0f172a] text-white'
                      : 'bg-[#E8D4BE]/50 text-[#0f172a] hover:bg-[#E8D4BE]'
                  }`}
                >
                  {Icon && <Icon size={18} />}
                  <span className="font-medium">{L(category, 'name')}</span>
                </button>
              );
            })}
          </div>
        </div>
      </nav>

      {/* Menu Content */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        {menuCategories.map((category) => {
          const Icon = iconMap[category.icon];
          const isExpanded = expandedMobile === category.id;
          
          return (
            <section
              key={category.id}
              id={category.id}
              className="mb-16 scroll-mt-40"
            >
              {/* Category Header */}
              <div 
                className="flex items-center justify-between cursor-pointer md:cursor-default mb-8"
                onClick={() => setExpandedMobile(isExpanded ? null : category.id)}
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-[#F5E6D3] rounded-full flex items-center justify-center">
                    {Icon && <Icon size={24} className="text-[#8B4513]" />}
                  </div>
                  <div>
                    <h2 className="font-serif text-2xl md:text-3xl font-bold text-[#0f172a]">
                      {L(category, 'name')}
                    </h2>
                    {category.subtitle && (
                      <p className="text-[#64748b] text-sm">{category.subtitle}</p>
                    )}
                  </div>
                </div>
                <button className="md:hidden p-2">
                  {isExpanded ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
                </button>
              </div>

              {/* Items Grid */}
              <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 ${!isExpanded && 'hidden md:grid'}`}>
                {category.items.map((item, index) => (
                  <div
                    key={index}
                    className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 group"
                  >
                    {item.image && (
                      <div className="aspect-[16/10] overflow-hidden">
                        <img
                          src={images[item.image]}
                          alt={item.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                    )}
                    <div className="p-5">
                      <div className="flex justify-between items-start gap-4 mb-2">
                        <h3 className="font-semibold text-[#0f172a] text-lg">
                          {L(item, 'name')}
                        </h3>
                        <span className="text-[#D4A853] font-bold whitespace-nowrap">
                          CHF {item.price.toFixed(2)}
                        </span>
                      </div>
                      {item.description && (
                        <p className="text-[#64748b] text-sm leading-relaxed">
                          {L(item, 'description')}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          );
        })}
      </div>

      {/* Info Banner */}
      <section className="bg-[#0f172a] py-12">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="text-white text-lg mb-4">
            {t('menu', 'takeaway')}
          </p>
          <p className="text-gray-400">
            {t('menu', 'allergies')}
          </p>
        </div>
      </section>
    </div>
  );
};

export default MenuPage;
